import { Button, Result, Stack } from '@pypestream/design-system';
import { FC, ReactNode, useEffect, useRef } from 'react';

/**
 * How many animation frames the focus move keeps trying (about a second at 60Hz)
 * when `focus()` does not take. A bound, not a promise: a screen that is still
 * not rendered after that is left with focus where it was.
 */
const FOCUS_RETRY_FRAMES = 60;

export type ErrorStateHeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export interface ErrorStateProps {
  /** Already-translated heading. Also the accessible name of the screen. */
  label: string;
  /** Already-translated explanation under the heading. */
  body: string;
  /** Content of the one action button. */
  actionLabel: ReactNode;
  /** What the action does. The caller decides; the package knows no route. */
  onAction: () => void;
  /** Heading level of `label`. Defaults to `h1`. */
  headingLevel?: ErrorStateHeadingLevel;
  /** `test-id` of the result element. */
  testId?: string;
  /** `test-id` of the action button. */
  actionTestId?: string;
  /** Shows the action as busy. */
  actionLoading?: boolean;
  /**
   * Move focus to the screen on mount when it was left on `<body>`. Defaults to
   * `true`; pass `false` where the caller already places focus itself.
   */
  autoFocus?: boolean;
}

/**
 * A centred error result with ONE action.
 *
 * Layout around it stays with the caller (`PageBody`, `PageSection`): the screens
 * this replaced sit in different containers, and the package holds no app text or
 * behaviour — the label, the body, the action's text and what it does all arrive
 * as props.
 *
 * Focus: a screen that replaces a spinner finds focus on `<body>` (WCAG 2.4.3).
 * The wrapper is a native node `tabIndex={-1}` can reach from script while keeping
 * it out of the tab order, and a named group so the move announces something.
 * LIMITATION: the heading sits in nested shadow roots, so focus cannot be put on
 * the heading itself. The wrapper's focus ring is suppressed; the button keeps its
 * own.
 */
export const ErrorState: FC<ErrorStateProps> = ({
  label,
  body,
  actionLabel,
  onAction,
  headingLevel = 'h1',
  testId,
  actionTestId,
  actionLoading,
  autoFocus = true,
}) => {
  const groupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!autoFocus) return;

    let frame: number | undefined;
    let framesLeft = FOCUS_RETRY_FRAMES;

    const tryFocus = () => {
      const group = groupRef.current;

      // Only while focus really is dropped, so it is never taken from where the
      // user has since moved. This also ends the loop once the group has it, and
      // makes a StrictMode replay harmless: the second run finds focus already
      // on the group, not on <body>, and does nothing.
      if (!group || document.activeElement !== document.body) return;

      group.focus();

      if (document.activeElement === group || framesLeft-- <= 0) return;

      // `focus()` did not take. On a first paint the design-system elements
      // around this screen are not upgraded yet, so the wrapper is not rendered
      // and the call is a silent no-op (measured on Manager's not-found page:
      // one call on a connected wrapper, <body> active before it, no move).
      // Try again on the next frame.
      frame = requestAnimationFrame(tryFocus);
    };

    tryFocus();

    return () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, [autoFocus]);

  return (
    <div
      ref={groupRef}
      tabIndex={-1}
      role="group"
      aria-label={label}
      style={{ outline: 'none' }}
    >
      <Result
        align="center"
        status="error"
        labelTag={headingLevel}
        test-id={testId}
        label={label}
        body={body}
      >
        <Stack gutter="xsmall" justifyContent="center" slot="footer">
          <Button
            size="large"
            test-id={actionTestId}
            loading={actionLoading}
            onClick={onAction}
          >
            {actionLabel}
          </Button>
        </Stack>
      </Result>
    </div>
  );
};
